import { useCallback, useEffect, useRef, useState } from 'react';
import { checkMFAStatus } from '@/lib/auth';
import { supabase } from '@/integrations/supabase/client';
import {
  classifyAuthEvent,
  createAuthSessionTransitionTracker,
  type AuthSessionTransitionKind,
} from '@/lib/authSessionTransitions';

/**
 * ENT-2770 — client-side AAL2 step-up gate for protected layouts.
 *
 * The layout MFA gates historically checked only *enrollment* ("does this
 * user have a verified factor?") via `listFactors`, never the *session*
 * assurance level. `signInWithPassword` persists a live **aal1** session
 * before the TOTP challenge is completed; the challenge exists only as
 * component-local state overlaying `/auth`. Navigating away from that
 * challenge — the Back button, a deep link, a restored tab — dropped an
 * enrolled user straight into the workspace, because `hasMFA` was already
 * true. This hook supplies the missing signal: when MFA is required and the
 * user is enrolled, the *session* must be aal2, or the layout renders a
 * step-up challenge instead of the workspace.
 *
 * Owns the *enrollment* determination too, via `checkMFAStatus().enrolled`,
 * so callers can enable it on the environment enforcement signal alone
 * (`isMfaRequired`) without threading an org-policy `hasMFA` flag. A user
 * with no factor is passed through as `satisfied` — there is nothing to step
 * up. This closes the voluntary-enrollment gap (Codex ENT-2770 P1):
 * `startSignInMfaFlow` challenges *every* enrolled account whenever Supabase
 * reports `nextLevel === 'aal2'`, independent of org `mfa_required`, so a user
 * who enrolled voluntarily in a non-MFA org can still reach an aal1 bypass
 * session — this gate covers them.
 *
 * The step-up target is a *verified* factor (`factorId`), which mirrors
 * sign-in's own trigger (`nextLevel === 'aal2'`). A user with only an
 * incomplete/unverified factor (abandoned TOTP setup) has `enrolled: true`
 * but `factorId: null`; they are passed through as `satisfied` — there is no
 * verified factor to challenge and sign-in would not challenge them either
 * (Codex ENT-2770 P1: don't strand them on an unrecoverable fallback).
 *
 * Fails **closed** only on a probe *error*: it resolves to `unavailable`
 * (workspace withheld, retry offered) rather than silently admitting the aal1
 * session. This honours ENT-1707 — a probe error must never be read as "aal2
 * satisfied" *or* as "no verified factor".
 */
export type AalStepUpStatus = 'checking' | 'satisfied' | 'step-up-required' | 'unavailable';

export interface AalStepUpGate {
  status: AalStepUpStatus;
  /** Verified factor to challenge for step-up; only set when `step-up-required`. */
  factorId: string | null;
  /** Re-run the assurance probe (call after a successful step-up verification). */
  recheck: () => void;
}

/**
 * The single assurance signal the state machine below maps to a status.
 * `verified` = the *session* is aal2; `factorId` = a *verified* factor to
 * challenge (only meaningful when `!verified`); `probeError` = the probe could
 * not be completed (transient failure) and must fail **closed** to
 * `unavailable`, never be read as "no factor" or "aal2 ok" (ENT-1707). A probe
 * may EITHER return `probeError: true` OR *reject* — both fail closed.
 */
export interface AssuranceProbeResult {
  verified: boolean;
  factorId: string | null;
  probeError?: boolean;
}

/**
 * A probe receives the running probe's `AbortSignal` so a *multi-call* probe can
 * bail between its Supabase requests once superseded, rather than firing a stale
 * follow-up call that contends for the navigator lock (Codex ENT-2818 P2). The
 * signal aborts when the probe effect re-runs or unmounts. Single-call probes
 * ignore it.
 */
export type AssuranceProbe = (signal: AbortSignal) => Promise<AssuranceProbeResult>;

/**
 * Layout probe — the coalesced `checkMFAStatus()` (ENT-1707) adapted to the
 * assurance-probe contract. Shared in-flight promise across callers, so the
 * session-swap path below drains it (`coalesced: true`) before trusting a
 * verdict. Single call, so the abort signal is not needed.
 */
const probeSessionAssuranceViaCheckMFAStatus: AssuranceProbe = async () => {
  const mfa = await checkMFAStatus();
  return { verified: mfa.verified, factorId: mfa.factorId, probeError: mfa.probeError };
};

/**
 * Claim-route probe (ENT-2818) — the `/vendor/claim` surface sits OUTSIDE
 * VendorLayout and its test harness mocks the raw Supabase MFA calls directly,
 * so this variant reads `listFactors` first and only calls
 * `getAuthenticatorAssuranceLevel` when a *verified* factor exists (a
 * factor-less user has nothing to step up). Not coalesced, so no drain is
 * needed on a session swap — the state machine's `active`/cleanup guard alone
 * discards a superseded probe's *result*. But this probe makes TWO sequential
 * calls, so it also honours the abort `signal` *between* them: if a swap
 * supersedes it while `listFactors()` is in flight, it stops before the stale
 * `getAuthenticatorAssuranceLevel()` — that follow-up would otherwise contend
 * for the navigator lock and could fail the valid replacement probe closed
 * (Codex ENT-2818 P2; the deleted copy checked `active` here). Fails closed by
 * *throwing* on a returned `{ error }` or a rejection (navigator-lock / aborted
 * request), which the state machine maps to `unavailable` (or ignores when the
 * throw is this probe's own abort, because `active` is already false).
 */
export const probeClaimRouteAssurance: AssuranceProbe = async (signal) => {
  const { data: factorsData, error: factorsError } = await supabase.auth.mfa.listFactors();
  if (factorsError) {
    throw factorsError;
  }
  const verifiedFactor = factorsData?.totp?.find((factor) => factor.status === 'verified');
  if (!verifiedFactor) {
    // No verified factor to challenge; nothing to step up.
    return { verified: false, factorId: null };
  }
  // Superseded while listFactors was pending — do NOT fire the stale AAL lookup.
  if (signal.aborted) {
    throw new DOMException('Superseded claim assurance probe', 'AbortError');
  }
  const { data: assuranceData, error: assuranceError } =
    await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (assuranceError || !assuranceData) {
    throw assuranceError ?? new Error('Missing MFA assurance data');
  }
  return { verified: assuranceData.currentLevel === 'aal2', factorId: verifiedFactor.id };
};

/**
 * The single session-assurance gate state machine. Every security invariant
 * that ENT-2770 re-discovered per copy lives here exactly once — synchronous
 * withhold on activation, canonical auth-event classification / baseline
 * bookkeeping (ENT-2928 tracker), superseded-probe `active` guard,
 * coalesced-probe drain, and fail-closed-on-error mapping. Callers differ only
 * in their `probe` (what to ask) and whether that probe is `coalesced` (whether
 * a session swap must drain a shared in-flight promise first). ENT-2818
 * consolidated the hand-rolled `/vendor/claim` copy onto this core.
 */
function useSessionAssuranceGate(
  enabled: boolean,
  probe: AssuranceProbe,
  coalesced: boolean,
  waitForBaseline: boolean,
): AalStepUpGate {
  const [status, setStatus] = useState<AalStepUpStatus>(enabled ? 'checking' : 'satisfied');
  const [factorId, setFactorId] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [prevEnabled, setPrevEnabled] = useState(enabled);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  // Probe + coalescing mode are held in refs so the effect deps stay
  // `[enabled, nonce]` (the wrappers pass stable module-level probes; refs also
  // guard against an unstable inline probe re-running the effect every render).
  const probeRef = useRef(probe);
  probeRef.current = probe;
  const coalescedRef = useRef(coalesced);
  coalescedRef.current = coalesced;
  const waitForBaselineRef = useRef(waitForBaseline);
  waitForBaselineRef.current = waitForBaseline;
  // Set once a session baseline (getSession / INITIAL_SESSION) has been observed,
  // tracked even while `enabled` is false so a surface that becomes enabled after
  // the baseline settled (leaving MSP mode) probes immediately instead of
  // withholding forever for a baseline that already arrived (Codex ENT-2818 P1).
  const baselineObservedRef = useRef(false);
  // Set when a session *swap* triggered the re-probe, so the probe effect can
  // drain any coalesced in-flight probe that may reflect the prior session
  // before trusting a fresh one (Codex ENT-2770 P1: coalescing race).
  const swapPendingRef = useRef(false);

  // Adjust assurance state *synchronously* when `enabled` flips (Codex
  // ENT-2770 P1). The probe runs in the effect below, which fires only after
  // commit — so on a false→true transition (a layout's enrollment check
  // completing) `status` would otherwise stay `satisfied` for one render and
  // let the protected body mount, its effects/queries/mutations running under
  // the aal1 session before the probe resolves. Setting state during render
  // makes React discard that render and re-render with `checking` before any
  // child commits. https://react.dev/reference/react/useState#storing-information-from-previous-renders
  if (enabled !== prevEnabled) {
    setPrevEnabled(enabled);
    setStatus(enabled ? 'checking' : 'satisfied');
    if (!enabled) setFactorId(null);
  }

  // Withhold *synchronously during render* whenever a re-probe is queued — a
  // session swap (the token effect below bumps `nonce` only on a tracker
  // 'probe' verdict, never a benign refresh, preserving ENT-2928 silent-adopt)
  // or an explicit `recheck`. Bumping `nonce` re-runs the probe effect, but that
  // fires only *after* commit; a stale mount/prev-session probe that resolves in
  // the same React batch as the swap event can run its `setStatus('satisfied')`
  // before the effect's `active`/cleanup guard takes hold, briefly committing
  // the protected surface under the replacement (aal1) session for one paint
  // (Codex ENT-2818 P1; the removed `/vendor/claim` copy closed this with a
  // render-time token withhold — ENT-2770 round-9). Forcing `checking` during
  // render makes React discard that render before any child commits, so the
  // stale `satisfied` never reaches the DOM. Idempotent and self-terminating
  // (`prevNonce` catches up), mirroring the `enabled` withhold above.
  const [prevNonce, setPrevNonce] = useState(nonce);
  if (enabled && nonce !== prevNonce) {
    setPrevNonce(nonce);
    setStatus('checking');
  } else if (nonce !== prevNonce) {
    setPrevNonce(nonce);
  }

  const recheck = useCallback(() => setNonce((n) => n + 1), []);

  // Re-probe whenever the authenticated session's access token *changes* —
  // a token refresh, or a cross-tab sign-in that swaps the shared session from
  // aal2 to aal1 (Codex ENT-2770 P1). Without this the probe only re-runs when
  // `enabled` flips (a stable user/enrollment signal), so a session swap would
  // keep the stale `satisfied` verdict and leave the workspace mounted under
  // aal1. The first token seen is recorded as a baseline (the probe effect
  // below already runs the initial probe on mount); only a *subsequent* change
  // withholds (`checking`) and re-probes.
  useEffect(() => {
    let active = true;
    // ENT-2928: classification and baseline bookkeeping live in the canonical
    // tracker (`@/lib/authSessionTransitions`) — this hook was the original
    // inline implementation (ENT-2872) and now consumes the shared one. The
    // semantics are unchanged: a `baseline` (the `getSession()` read /
    // `INITIAL_SESSION`) seeds without re-probing (the mount probe below
    // already measures it); a benign `token-refresh` with a baseline present
    // is adopted silently (re-gating the mounted workspace for it was the
    // ENT-2872 staging-only remount); a genuine `swap` — or a refresh racing
    // ahead of the baseline — withholds (`checking`) and re-probes, because a
    // cross-tab sign-in can replace an aal2 session with an aal1 one.
    const tracker = createAuthSessionTransitionTracker();
    const consider = (kind: AuthSessionTransitionKind, token?: string | null) => {
      if (!active) return;
      const action = tracker.consider(kind, token);
      // Record baseline observation regardless of `enabled` (see ref comment).
      if (tracker.hasBaseline) baselineObservedRef.current = true;
      if (!enabledRef.current) return;
      if (action === 'probe') {
        swapPendingRef.current = true;
        setStatus('checking');
        setNonce((n) => n + 1);
        return;
      }
      // Claim path (waitForBaseline): the FIRST probe is withheld (below) until a
      // session baseline is observed, so it measures the SETTLED session, not one
      // that a verification-link / cross-tab replacement is about to swap out —
      // the removed /vendor/claim copy's `claimSessionToken === undefined` wait
      // that the consolidation dropped (Codex ENT-2818 P1). A 'seed' releases it.
      if (action === 'seed' && waitForBaselineRef.current) {
        setNonce((n) => n + 1);
      }
    };
    supabase.auth.getSession().then(({ data }) => consider('baseline', data.session?.access_token));
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) =>
      consider(classifyAuthEvent(event), session?.access_token),
    );
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      setStatus('satisfied');
      setFactorId(null);
      return;
    }

    // Claim path: defer the very FIRST probe until a session baseline has been
    // observed (the token effect above bumps `nonce` on the seed), so the probe
    // measures the settled session rather than one being replaced mid-mount
    // (Codex ENT-2818 P1). Once any observation has occurred (`nonce > 0`) or a
    // baseline already exists (e.g. seeded while disabled, then enabled), probe
    // normally. The layout path (`waitForBaseline` false) is unaffected — it
    // probes immediately on mount as before.
    if (waitForBaselineRef.current && nonce === 0 && !baselineObservedRef.current) {
      setStatus('checking');
      return;
    }

    let active = true;
    // Aborted in cleanup so a *multi-call* probe (the claim route) can stop
    // before firing a stale follow-up request once superseded (Codex ENT-2818
    // P2). `active` still guards every state write; the signal additionally
    // prevents the wasted, lock-contending call itself.
    const controller = new AbortController();
    setStatus('checking');

    // If this run was triggered by a session swap and the probe is *coalesced*
    // (a shared in-flight promise, e.g. `checkMFAStatus`), an earlier probe may
    // still be in flight for the *previous* session and would hand back its
    // (stale aal2) result. Drain that in-flight probe first, then run a
    // guaranteed-fresh one for the current session (Codex ENT-2770 P1). A
    // non-coalesced probe (the claim route's raw calls) needs no drain — the
    // `active` guard alone discards a superseded probe.
    const drainFirst = swapPendingRef.current && coalescedRef.current;
    swapPendingRef.current = false;

    (async () => {
      if (drainFirst) {
        await probeRef.current(controller.signal).catch(() => undefined);
        if (!active) return;
      }

      let result: AssuranceProbeResult;
      try {
        result = await probeRef.current(controller.signal);
      } catch (probeError) {
        // Superseded (including this probe's own abort throw once a newer run
        // took over) → `active` is false; do nothing and do NOT log — the abort
        // is expected control flow, not a diagnostic signal.
        if (!active) return;
        // Genuine probe failure (navigator-lock contention, network, malformed
        // response, listFactors/getAAL error). Log the concrete cause so
        // operators can distinguish it — the user only sees the generic
        // recovery card (Codex ENT-2818 P2) — then fail closed to `unavailable`.
        console.error('Session-assurance probe failed; withholding (fail-closed):', probeError);
        setFactorId(null);
        setStatus('unavailable');
        return;
      }
      if (!active) return;

      if (result.probeError) {
        // Probe itself failed — never infer assurance *or* enrollment from
        // it (ENT-1707). Fail closed.
        setFactorId(null);
        setStatus('unavailable');
        return;
      }
      if (result.verified) {
        // Session is already aal2 — nothing to step up.
        setFactorId(result.factorId);
        setStatus('satisfied');
        return;
      }
      if (result.factorId) {
        // A *verified* factor exists but the session is aal1 — challenge it.
        setFactorId(result.factorId);
        setStatus('step-up-required');
        return;
      }
      // No verified factor to challenge. `factorId` is the only reliable
      // "can be stepped up" signal — it mirrors sign-in's own trigger, which
      // fires only when Supabase reports `nextLevel === 'aal2'` (i.e. a
      // verified factor exists). An *unverified* factor (abandoned TOTP setup)
      // is deliberately NOT a challenge target — treating it as one would
      // strand the user on an unrecoverable fallback (Codex ENT-2770 P1).
      // Sign-in would not challenge them either, so pass through as satisfied.
      setFactorId(null);
      setStatus('satisfied');
    })();

    return () => {
      active = false;
      controller.abort();
    };
  }, [enabled, nonce]);

  return { status, factorId, recheck };
}

/**
 * ENT-2770 layout gate — session-assurance step-up for protected layouts
 * (`AppLayout`, `AdminLayout`, `VendorLayout`). Probes via the coalesced
 * `checkMFAStatus()` and passes non-enrolled users through as `satisfied`.
 *
 * @param enabled Pass `true` when MFA is enforced for the current environment
 * (e.g. `!isPreviewMfaBypass()`). Enrollment is determined *inside* the gate,
 * so the caller does not pre-check it: a non-enrolled user resolves to
 * `satisfied`. When `false` the gate is inert and reports `satisfied` (this
 * preserves the ENT-1418 local/preview bypass).
 */
export function useAalStepUpGate(enabled: boolean): AalStepUpGate {
  // waitForBaseline: false — the layout hook probes immediately on mount and
  // re-probes on swaps; its coalesced-probe drain + `active` guard handle the
  // in-flight-stale case (the mount-during-replacement window is closed by the
  // claim path's baseline-wait, which the layouts do not need).
  return useSessionAssuranceGate(enabled, probeSessionAssuranceViaCheckMFAStatus, true, false);
}

/**
 * ENT-2818 claim-route gate — the same session-assurance state machine as
 * `useAalStepUpGate`, differing only in its probe: the `/vendor/claim` surface
 * lives outside VendorLayout and reads the raw Supabase MFA calls directly
 * (`listFactors` first, `getAuthenticatorAssuranceLevel` only when a verified
 * factor exists). The caller (`VendorClaim`) composes the enrollment/step-up
 * *redirects* on top of the returned status. `enabled` is the same carve-out
 * sign-in uses (`!isPreviewMfaBypass()`), additionally gated off the MSP claim
 * flow; leaving MSP mode flips `enabled` and the core's activation withhold
 * blocks the direct-mode controls synchronously (Codex ENT-2770 round-9).
 */
export function useClaimRouteStepUpGate(enabled: boolean): AalStepUpGate {
  // waitForBaseline: true — the claim route's RPC (`claim_vendor_profile_with_
  // workspace`) checks identity/domain but NOT AAL (ENT-2773), so the client
  // gate is the only barrier and must never expose claim controls under a
  // session it has not actually measured. Deferring the first probe until a
  // baseline is observed closes the mount-during-replacement race (Codex P1).
  return useSessionAssuranceGate(enabled, probeClaimRouteAssurance, false, true);
}
